"use client"

import { VOICE_KNOWLEDGE_PENDING_MESSAGE } from "@/lib/voice/elevenlabs-sync/status"

import { useQuery } from "@tanstack/react-query"

import type { VoiceAgent } from "@/hooks/agents/voice/use-agents"
import { useAuth } from "@/hooks/use-auth"
import { useTranslations } from "next-intl"


export const voiceAgentQueryKey = (agentId: string) => [
  "voice-agent",
  agentId,
] as const

export function useVoiceAgentQuery(agentId: string | null) {
  const tUi = useTranslations("Feedback.QueriesVoiceAgent")

  const { fetchWithAuth } = useAuth()

  return useQuery<VoiceAgent>({
    queryKey: voiceAgentQueryKey(agentId ?? "none"),
    enabled: Boolean(agentId),
    refetchInterval: query => {
      const agent = query.state.data
      if (agent && (!agent.external_id || agent.last_sync_error === VOICE_KNOWLEDGE_PENDING_MESSAGE)) return 5000
      return false
    },
    queryFn: async () => {
      if (!agentId) throw new Error(tUi("phoneVoiceAgentIdIsRequired"))
      const response = await fetchWithAuth(`/api/agents/voice/${agentId}`)
      const result = (await response.json()) as {
        agent?: VoiceAgent
        error?: string
      }
      if (!response.ok || !result.agent) {
        throw new Error(result.error ?? "Failed to load Phone Voice Agent.")
      }
      return result.agent
    },
  })
}
